import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import SEO from '../../components/SEO';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import MetricsCard from '../dashboard/components/MetricsCard';
import ProjectPhaseChart from './components/ProjectPhaseChart';
import RevenueAnalytics from './components/RevenueAnalytics';
import PermitPerformance from './components/PermitPerformance';
import ContractorMetrics from './components/ContractorMetrics';
import TeamProductivity from './components/TeamProductivity';
import PermissionDenied from '../../components/ui/PermissionDenied';
import { usePermissions } from '../../hooks/usePermissions';
import { analyticsService } from '../../services/analyticsService';
import { supabase } from '../../lib/supabase';
import { exportToCSV } from '../../utils/exportToCSV';

const ReportsAnalytics = () => {
  const permissions = usePermissions();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [projectType, setProjectType] = useState('all');
  const [exportError, setExportError] = useState('');
  const subscriptionsRef = useRef([]);
  
  // Analytics data state
  const [projectsByPhase, setProjectsByPhase] = useState([]);
  const [revenuePipeline, setRevenuePipeline] = useState([]);
  const [permitPerformance, setPermitPerformance] = useState({ approvalRate: 0, avgProcessingTime: 0, statusBreakdown: [] });
  const [contractorPerformance, setContractorPerformance] = useState([]);
  const [teamProductivity, setTeamProductivity] = useState({ activityByUser: [], activityByAction: [], recentActivity: [] });

  // Summary metrics
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalProjects: 0,
    totalRevenue: 0,
    avgPermitTime: 0,
    activeContractors: 0
  });

  // Re-fetch whenever filters change
  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange, projectType]);

  // Set up real-time subscriptions for live metric updates
  useEffect(() => {
    const projectsSub = supabase
      ?.channel('reports-projects')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'projects' }, () => {
        fetchAnalyticsData();
      })
      ?.subscribe();

    const permitsSub = supabase
      ?.channel('reports-permits')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'permits' }, () => {
        fetchAnalyticsData();
      })
      ?.subscribe();

    const contractorsSub = supabase
      ?.channel('reports-contractors')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'contractors' }, () => {
        fetchAnalyticsData();
      })
      ?.subscribe();

    const activitySub = supabase
      ?.channel('reports-activity')
      ?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'activity_logs' }, () => {
        fetchAnalyticsData();
      })
      ?.subscribe();

    subscriptionsRef.current = [projectsSub, permitsSub, contractorsSub, activitySub];

    return () => {
      subscriptionsRef?.current?.forEach(sub => {
        if (sub) supabase?.removeChannel(sub);
      });
    };
  }, []);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      // Pass active filters to every service call so queries are scoped server-side
      const [projects, revenue, permits, contractors, team] = await Promise.all([
        analyticsService?.getProjectsByPhase(dateRange, projectType),
        analyticsService?.getRevenuePipeline(dateRange, projectType),
        analyticsService?.getPermitPerformance(dateRange),
        analyticsService?.getContractorPerformance(),
        analyticsService?.getTeamProductivity(dateRange)
      ]);

      setProjectsByPhase(projects);
      setRevenuePipeline(revenue);
      setPermitPerformance(permits);
      setContractorPerformance(contractors);
      setTeamProductivity(team);

      // Calculate summary metrics
      const totalProjects = projects?.reduce((sum, p) => sum + (p?.count || 0), 0);
      const totalRevenue = revenue?.reduce((sum, r) => sum + (r?.actual || 0), 0);
      
      setSummaryMetrics({
        totalProjects,
        totalRevenue: totalRevenue || 0,
        avgPermitTime: permits?.avgProcessingTime || 0,
        activeContractors: contractors?.length || 0
      });
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const dateRangeOptions = [
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: '365', label: 'Last year' },
    { value: 'all', label: 'All time' }
  ];

  const projectTypeOptions = [
    { value: 'all', label: 'All Projects' },
    { value: 'detached', label: 'Detached ADU' },
    { value: 'attached', label: 'Attached ADU' },
    { value: 'garage_conversion', label: 'Garage Conversion' },
    { value: 'basement_conversion', label: 'Basement Conversion' }
  ];

  const handleExport = () => {
    if (!permissions?.canCreate) {
      setExportError('Export is restricted to admin and operator roles only.');
      setTimeout(() => setExportError(''), 4000);
      return;
    }

    const dateLabel = dateRangeOptions?.find(o => o?.value === dateRange)?.label || dateRange;
    const typeLabel = projectTypeOptions?.find(o => o?.value === projectType)?.label || projectType;
    const timestamp = new Date()?.toISOString()?.slice(0, 10);

    // Revenue Pipeline
    if (revenuePipeline?.length) {
      exportToCSV(
        revenuePipeline,
        [
          { key: 'month', label: 'Month' },
          { key: 'projected', label: 'Projected Revenue ($)' },
          { key: 'actual', label: 'Actual Revenue ($)' }
        ],
        `revenue_pipeline_${timestamp}`
      );
    }

    // Project Phases
    if (projectsByPhase?.length) {
      exportToCSV(
        projectsByPhase,
        [
          { key: 'phase', label: 'Phase' },
          { key: 'count', label: 'Project Count' }
        ],
        `project_phases_${timestamp}`
      );
    }

    // Permit Performance
    if (permitPerformance?.statusBreakdown?.length) {
      const permitRows = [
        { metric: 'Approval Rate (%)', value: permitPerformance?.approvalRate ?? 0 },
        { metric: 'Avg Processing Time (days)', value: permitPerformance?.avgProcessingTime ?? 0 },
        ...permitPerformance?.statusBreakdown?.map(s => ({ metric: `Status: ${s?.status}`, value: s?.count ?? 0 }))
      ];
      exportToCSV(
        permitRows,
        [
          { key: 'metric', label: 'Metric' },
          { key: 'value', label: 'Value' }
        ],
        `permit_performance_${timestamp}`
      );
    }
  };

  const isViewer = permissions?.role === 'viewer';

  // Safe revenue display: guard against NaN when totalRevenue is 0 or undefined
  const revenueDisplay = isViewer
    ? '—'
    : `$${((summaryMetrics?.totalRevenue || 0) / 1000)?.toFixed(0)}K`;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Reports & Analytics — ADU Business Performance Insights"
        description="Analyze your ADU construction business performance. Track revenue, project phases, permit success rates, contractor metrics, and team productivity with ADU Flow analytics."
        canonical="/reports-analytics"
        keywords="ADU reports, construction analytics, business performance, revenue tracking, permit metrics"
        noIndex={true}
      />
      <Header />
      
      <main className="main-content">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
                <Icon name="BarChart3" size={32} className="text-primary" />
                Reports & Analytics
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Comprehensive business intelligence and performance metrics
              </p>
            </div>
            
            <Button
              variant="outline"
              iconName="Download"
              onClick={handleExport}
              className="w-full sm:w-auto"
              disabled={isViewer}
            >
              Export Report
            </Button>
          </div>

          {/* Export Permission Error */}
          {exportError && (
            <PermissionDenied message={exportError} />
          )}

          {/* Viewer Limited Access Banner */}
          {isViewer && (
            <div className="flex items-center gap-3 px-4 py-3 bg-warning/10 border border-warning/20 rounded-lg text-sm text-warning">
              <Icon name="Info" size={18} className="flex-shrink-0" />
              <span>You have read-only access. Revenue details and export functionality are restricted to admin and operator roles.</span>
            </div>
          )}

          {/* Filters */}
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Select
                label="Date Range"
                options={dateRangeOptions}
                value={dateRange}
                onChange={setDateRange}
              />
              <Select
                label="Project Type"
                options={projectTypeOptions}
                value={projectType}
                onChange={setProjectType}
              />
              <div className="flex items-end">
                <Button
                  variant="secondary"
                  iconName="RefreshCw"
                  onClick={fetchAnalyticsData}
                  loading={loading}
                  fullWidth
                >
                  Refresh Data
                </Button>
              </div>
            </div>
          </div>

          {/* Summary Metrics - visible to all */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricsCard
              title="Total Projects"
              value={summaryMetrics?.totalProjects?.toString()}
              subtitle="Across all phases"
              icon="Briefcase"
              variant="primary"
              trend="up"
              trendValue="0"
            />
            <MetricsCard
              title="Total Revenue"
              value={revenueDisplay}
              subtitle={isViewer ? 'Restricted' : 'Actual revenue'}
              icon="DollarSign"
              variant="success"
              trend="up"
              trendValue="0"
            />
            <MetricsCard
              title="Avg Permit Time"
              value={`${summaryMetrics?.avgPermitTime} days`}
              subtitle="Processing time"
              icon="Clock"
              variant="warning"
              trend="neutral"
              trendValue="0"
            />
            <MetricsCard
              title="Active Contractors"
              value={summaryMetrics?.activeContractors?.toString()}
              subtitle="Top performers"
              icon="HardHat"
              variant="accent"
              trend="up"
              trendValue="0"
            />
          </div>

          {/* Charts Grid - restricted for viewers */}
          {!isViewer ? (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ProjectPhaseChart data={projectsByPhase} loading={loading} />
                <RevenueAnalytics data={revenuePipeline} loading={loading} />
              </div>
              <PermitPerformance data={permitPerformance} loading={loading} />
              <ContractorMetrics data={contractorPerformance} loading={loading} />
              <TeamProductivity data={teamProductivity} loading={loading} />
            </>
          ) : (
            <>
              {/* Viewers see project phase chart only */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ProjectPhaseChart data={projectsByPhase} loading={loading} />
                <div className="bg-card border border-border rounded-xl p-6 flex flex-col items-center justify-center min-h-[300px]">
                  <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4">
                    <Icon name="Lock" size={28} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">Revenue Data Restricted</h3>
                  <p className="text-sm text-muted-foreground text-center max-w-xs">
                    Revenue analytics are available to admin and operator roles only.
                  </p>
                </div>
              </div>
              <PermitPerformance data={permitPerformance} loading={loading} />
              <div className="bg-card border border-border rounded-xl p-6 flex flex-col items-center justify-center min-h-[200px]">
                <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Icon name="Lock" size={28} className="text-muted-foreground" />
                </div>
                <h3 className="text-base font-semibold text-foreground mb-2">Contractor & Team Data Restricted</h3>
                <p className="text-sm text-muted-foreground text-center max-w-xs">
                  Detailed contractor metrics and team productivity data are available to admin and operator roles only.
                </p>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ReportsAnalytics;